import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Stack,
  TextField,
  Typography,
  styled,
} from "@mui/material";
import Swal from "sweetalert2";
import { axiosClient } from "../AxiosClient";
import { PRAMI_COLORS } from "../expedicionCertificados/certificadosTheme";
import { formatCreditOldId, formatMoney, roundMoney, squareFieldSx } from "./condonationFormat";

const ButtonAdd = styled(Button)({
  borderRadius: "20px !important",
  backgroundColor: "#161e55 !important",
  color: "#ffff !important",
  "&:hover": { backgroundColor: "#A8C8AF !important" },
});

/**
 * Acción rápida para normalizar al cliente: crea un conjunto de ofertas de mora y/o GAC, una por cuota
 * vencida marcada (POST /v1/condonations/credits/{uuid}/mora-gac-offers). Comparten fecha y monto
 * acordado. Si al vencer no se pagó ese monto, el cron levanta todas.
 */
export default function CondonarMoraGacDialog({
  open,
  credit,
  selection = [],
  overdueQuotas = [],
  onClose,
  onSuccess,
}) {
  const [expirationDate, setExpirationDate] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }
    setExpirationDate("");
    setReason("");
    setSubmitting(false);
  }, [open]);

  const totals = useMemo(() => {
    let gacTotal = 0;
    let moraTotal = 0;
    let selectedSaldoTotal = 0;
    let quotasCount = 0;
    selection.forEach((item) => {
      if (item.gac) {
        gacTotal += Number(item.activeQuota?.pending?.pendingGac || 0);
      }
      if (item.mora) {
        moraTotal += Number(item.activeQuota?.pending?.pendingMora || 0);
      }
      if (item.gac || item.mora) {
        selectedSaldoTotal += Number(item.activeQuota?.pending?.totalPending || 0);
        quotasCount += 1;
      }
    });
    // Descuento = 100% de la mora y/o el GAC marcados en las cuotas seleccionadas.
    const discountTotal = gacTotal + moraTotal;
    // Saldo de TODAS las cuotas vencidas (estén o no seleccionadas).
    const overdueSaldoTotal = overdueQuotas.reduce(
      (sum, activeQuota) => sum + Number(activeQuota?.pending?.totalPending || 0),
      0
    );
    return {
      gacTotal: roundMoney(gacTotal),
      moraTotal: roundMoney(moraTotal),
      // Suma a pagar de cuotas seleccionadas: saldo de las cuotas seleccionadas menos el descuento
      // de mora/GAC de esas cuotas.
      saldoTotal: roundMoney(selectedSaldoTotal - discountTotal),
      // Valor a pagar para normalizar: saldo de TODAS las cuotas vencidas menos el descuento de
      // mora/GAC de las cuotas seleccionadas = lo que el cliente pagaría para ponerse al día.
      normalizationTotal: roundMoney(overdueSaldoTotal - discountTotal),
      quotasCount,
    };
  }, [selection, overdueQuotas]);

  if (!credit) {
    return null;
  }

  const warn = (title, text) => Swal.fire({ icon: "warning", title, text, confirmButtonColor: "#161e55" });

  const submit = () => {
    if (!expirationDate) {
      warn("Falta la fecha", "Indica la fecha de vencimiento de la oferta.");
      return;
    }
    if (!reason.trim()) {
      warn("Falta el motivo", "Indica el motivo de la condonación.");
      return;
    }
    if (totals.quotasCount === 0) {
      warn("Falta la selección", "Marca la mora y/o el GAC de al menos una cuota para condonar.");
      return;
    }

    const userUuid = localStorage.getItem("userUuid") || null;
    const quotas = selection
      .filter((item) => item.gac || item.mora)
      .map((item) => ({
        quotaUuid: item.activeQuota.quota.uuid,
        gac: !!item.gac,
        mora: !!item.mora,
      }));

    setSubmitting(true);
    axiosClient
      .post(`/v1/condonations/credits/${credit.uuid}/mora-gac-offers`, {
        expirationDate,
        freezeGacAndMora: true,
        reason: reason.trim(),
        createdByUserUuid: userUuid,
        quotas,
      })
      .then((response) => {
        setSubmitting(false);
        const count = (response.data?.offers || []).length || quotas.length;
        onSuccess(
          `Se crearon ${count} oferta(s) de mora/GAC en el crédito ${formatCreditOldId(credit)}.`
        );
        onClose();
      })
      .catch((error) => {
        setSubmitting(false);
        Swal.fire({
          icon: "error",
          title: "No se pudo crear la oferta",
          text:
            error?.response?.data?.message ||
            error?.response?.data?.description ||
            "Revisa los datos ingresados.",
          confirmButtonColor: "#161e55",
        });
      });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: "1.1rem", fontWeight: 700 }}>
        {`Condonar mora y/o GAC · crédito ${formatCreditOldId(credit)}`}
      </DialogTitle>
      <DialogContent
        sx={{
          ...squareFieldSx,
          "& .MuiTypography-body2": { fontSize: "0.8rem" },
          "& .MuiTypography-caption": { fontSize: "0.7rem" },
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Se creará una oferta de condonación del <strong>100%</strong> del componente marcado en cada
          cuota vencida seleccionada, para normalizar al cliente.
        </Typography>

        <Box
          sx={{
            mb: 2,
            p: 2,
            bgcolor: PRAMI_COLORS.pageBg,
            border: `1px solid ${PRAMI_COLORS.surfaceBorder}`,
            borderRadius: 2,
            display: "flex",
            gap: 3,
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Typography variant="caption" color="text.secondary">
              Cuotas seleccionadas
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, color: PRAMI_COLORS.primary }}>
              {totals.quotasCount}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              GAC a condonar
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, color: PRAMI_COLORS.primary }}>
              {formatMoney(totals.gacTotal)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Mora a condonar
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700, color: PRAMI_COLORS.primary }}>
              {formatMoney(totals.moraTotal)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Suma a pagar de cuotas seleccionadas
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontWeight: 700, color: PRAMI_COLORS.primary, fontSize: "1.05rem" }}
            >
              {formatMoney(totals.saldoTotal)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Valor a pagar para normalizar
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontWeight: 700, color: PRAMI_COLORS.primary, fontSize: "1.05rem" }}
            >
              {formatMoney(totals.normalizationTotal)}
            </Typography>
          </Box>
        </Box>

        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2, gap: 1 }}>
          {selection
            .filter((item) => item.gac || item.mora)
            .map((item) => {
              const parts = [];
              if (item.gac) parts.push("GAC");
              if (item.mora) parts.push("Mora");
              return (
                <Chip
                  key={item.activeQuota.quota.uuid}
                  size="small"
                  variant="outlined"
                  sx={{ borderColor: PRAMI_COLORS.surfaceBorder }}
                  label={`Cuota ${item.activeQuota.quota.quotaNumber}: ${parts.join(" + ")}`}
                />
              );
            })}
        </Stack>

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              type="date"
              label="Fecha de vencimiento"
              InputLabelProps={{ shrink: true }}
              value={expirationDate}
              onChange={(event) => setExpirationDate(event.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Motivo"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <ButtonAdd disabled={submitting} onClick={submit}>
          Crear oferta
        </ButtonAdd>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}
